import dayjs from '@/lib/dayjs'
import { holidayLabelsById } from './designs.constants'
import type { Design, DesignSchedule } from './designs.types'

export interface DesignScheduleSummary {
  isScheduled: boolean
  text: string
}

const formatScheduleDate = (value: string | null) => {
  if (!value) {
    return null
  }

  const parsed = dayjs(value)
  return parsed.isValid() ? parsed.format('MMM D, YYYY') : null
}

const formatDayCount = (value: number | null) => {
  const days = Number(value ?? 0)
  const absoluteDays = Math.abs(days)
  const dayLabel = absoluteDays === 1 ? 'day' : 'days'

  return { days, absoluteDays, dayLabel }
}

const describeHolidayOffset = (holidayKey: string | null, offsetDays: number | null) => {
  const holidayName = (holidayKey && holidayLabelsById[holidayKey]) || 'selected holiday'
  const { days, absoluteDays, dayLabel } = formatDayCount(offsetDays)

  if (days === 0) {
    return `on ${holidayName}`
  }

  if (days < 0) {
    return `${absoluteDays} ${dayLabel} before ${holidayName}`
  }

  return `${absoluteDays} ${dayLabel} after ${holidayName}`
}

const getStartSummary = (schedule: DesignSchedule) => {
  if (schedule.start_type === 'fixed_date') {
    const startsOn = formatScheduleDate(schedule.starts_on)
    if (!startsOn) {
      return 'start date is not set'
    }

    if (schedule.is_yearly) {
      return `starts ${startsOn} each year`
    }

    return `starts ${startsOn}`
  }

  if (schedule.start_type === 'holiday_offset') {
    return `starts ${describeHolidayOffset(schedule.start_holiday, schedule.start_offset_days)}`
  }

  return 'start rule is incomplete'
}

const getEndSummary = (schedule: DesignSchedule) => {
  const endType = schedule.end_type || 'none'

  if (endType === 'none') {
    return 'has no automatic end date'
  }

  if (endType === 'fixed_date') {
    const endsOn = formatScheduleDate(schedule.ends_on)
    return endsOn ? `ends ${endsOn}` : 'has an incomplete end date'
  }

  if (endType === 'holiday_offset') {
    return `ends ${describeHolidayOffset(schedule.end_holiday, schedule.end_offset_days)}`
  }

  if (endType === 'days_after_start') {
    const { absoluteDays, dayLabel } = formatDayCount(schedule.end_days_after_start)
    return `ends ${absoluteDays} ${dayLabel} after it starts`
  }

  return 'has an incomplete end rule'
}

const getActiveResetSummary = (schedule: DesignSchedule) => {
  const endType = schedule.end_type || 'none'

  if (endType === 'none') {
    return 'No automatic reset date.'
  }

  if (endType === 'fixed_date') {
    const endsOn = formatScheduleDate(schedule.ends_on)
    return endsOn ? `Auto-resets to default on ${endsOn}.` : 'Auto-reset rule is incomplete.'
  }

  if (endType === 'holiday_offset') {
    return `Auto-resets to default ${describeHolidayOffset(schedule.end_holiday, schedule.end_offset_days)}.`
  }

  if (endType === 'days_after_start') {
    const { absoluteDays, dayLabel } = formatDayCount(schedule.end_days_after_start)
    return `Auto-resets to default ${absoluteDays} ${dayLabel} after start.`
  }

  return 'Auto-reset rule is incomplete.'
}

export const getDesignScheduleSummary = ({
  design,
  isActive,
}: {
  design: Pick<Design, 'is_default' | 'schedule'> | null | undefined
  isActive: boolean
}): DesignScheduleSummary => {
  if (design?.is_default) {
    return {
      isScheduled: false,
      text: 'Not scheduled. This is your fallback default design.',
    }
  }

  const schedule = design?.schedule

  if (!schedule) {
    return {
      isScheduled: false,
      text: 'Not scheduled.',
    }
  }

  if (!schedule.is_enabled) {
    return {
      isScheduled: false,
      text: 'Schedule is saved but currently off.',
    }
  }

  if (isActive) {
    return {
      isScheduled: true,
      text: `Active now. ${getActiveResetSummary(schedule)}`,
    }
  }

  return {
    isScheduled: true,
    text: `${getStartSummary(schedule)} and ${getEndSummary(schedule)}.`,
  }
}
